import React from 'react';

interface ErrorBoundaryProps {
  fallback?: React.ReactNode;
  children: React.ReactNode;
  onReportError?: (error: Error) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div style={{
          padding: 'var(--space-xl)',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 'var(--space-md)',
        }}>
          <div style={{ fontSize: 'var(--font-size-2xl)', color: 'var(--color-error)' }}>
            Something went wrong
          </div>
          <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)', maxWidth: 400 }}>
            {this.state.error?.message ?? 'An unexpected error occurred.'}
          </div>
          <button
            onClick={this.handleRetry}
            aria-label="Retry loading this section"
            style={{
              padding: 'var(--space-sm) var(--space-lg)',
              background: 'var(--color-primary)',
              color: 'white',
              borderRadius: 'var(--border-radius-sm)',
              fontWeight: 'var(--font-weight-semibold)',
            }}
          >
            Try Again
          </button>
          {this.props.onReportError && this.state.error && (
            <button
              onClick={() => this.props.onReportError!(this.state.error!)}
              aria-label="Report this error"
              style={{
                padding: 'var(--space-sm) var(--space-lg)',
                background: 'transparent',
                color: 'var(--color-primary)',
                borderRadius: 'var(--border-radius-sm)',
                border: '1px solid var(--color-primary)',
                fontWeight: 'var(--font-weight-semibold)',
              }}
            >
              Report this error
            </button>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
